import { useState } from 'react';
import CapabilitiesPanel from './CapabilitiesPanel';
import { DETAIL_FIELDS } from '../interview';
import { PROJECT_TYPES, type Brief } from '../types';

const inputCls =
  'w-full bg-bg border border-border rounded-md px-2.5 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent transition-colors';

type Props = {
  brief: Brief;
  step: number;
  selected: string[];
  continueErr: string | null;
  continueBusy: boolean;
  onUpdate: (patch: Partial<Brief>) => void;
  onToggleCapability: (id: string) => void;
  onClearCapabilities: () => void;
  onContinue: () => void;
};

function FieldBlock({
  label,
  active,
  children,
}: {
  label: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`rounded-lg border p-3 transition-colors ${active ? 'border-accent' : 'border-border'}`}>
      <div className="text-[11px] uppercase tracking-wider text-texts font-medium mb-2">{label}</div>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-texts font-medium mb-0.5">{label}</dt>
      <dd className="text-sm text-textp leading-snug whitespace-pre-wrap">{value || '—'}</dd>
    </div>
  );
}

export default function BriefForm({
  brief,
  step,
  selected,
  continueErr,
  continueBusy,
  onUpdate,
  onToggleCapability,
  onClearCapabilities,
  onContinue,
}: Props) {
  const [tab, setTab] = useState<'capabilities' | 'brief'>('brief');
  const completed = step >= 5;
  const detail = brief.type ? DETAIL_FIELDS[brief.type] : null;

  return (
    <aside
      className="flex flex-col min-h-0 w-full lg:w-[360px] h-[340px] lg:h-auto bg-surface border-t lg:border-t-0 lg:border-l border-border shrink-0"
      data-testid="composer-rail"
    >
      <div className="px-3 pt-3 shrink-0">
        <div className="flex items-center gap-1 bg-surface2 rounded-full p-1">
          <button
            type="button"
            data-testid="composer-tab-capabilities"
            onClick={() => setTab('capabilities')}
            className={`whitespace-nowrap flex-1 text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${
              tab === 'capabilities' ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
            }`}
          >
            Capabilities
          </button>
          <button
            type="button"
            data-testid="composer-tab-brief"
            onClick={() => setTab('brief')}
            className={`whitespace-nowrap flex-1 text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${
              tab === 'brief' ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
            }`}
          >
            Brief
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {tab === 'capabilities' ? (
          <CapabilitiesPanel
            selected={selected}
            onToggle={onToggleCapability}
            onClear={onClearCapabilities}
          />
        ) : (
          <div className="p-4 space-y-3.5" data-testid="composer-brief">
            {completed ? (
              <div className="space-y-3.5">
                <div className="rounded-lg bg-accentSoft border border-accentLine p-3 flex gap-2">
                  <i className="ri-checkbox-circle-line text-accent text-lg shrink-0" />
                  <p className="text-xs text-textp leading-relaxed">
                    Brief complete. Review the details below, then continue building.
                  </p>
                </div>

                <dl className="space-y-2.5">
                  <SummaryRow label="Name" value={brief.name} />
                  <SummaryRow label="Type" value={brief.type || ''} />
                  <SummaryRow label="Description" value={brief.description} />
                  {brief.detail && <SummaryRow label={detail?.label || 'Detail'} value={brief.detail} />}
                  {brief.features && <SummaryRow label="Features" value={brief.features} />}
                </dl>

                {continueErr && (
                  <p data-testid="composer-continue-error" className="text-xs text-error">
                    {continueErr}
                  </p>
                )}
                <button
                  type="button"
                  data-testid="composer-continue-build"
                  disabled={continueBusy}
                  onClick={onContinue}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-1.5 bg-accent hover:bg-accent-hover text-accent-text text-sm font-medium px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60"
                >
                  Continue building
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-texts leading-relaxed">
                    The brief fills in as we talk. Edit any field directly.
                  </p>
                  <span className="text-[10px] text-texts font-mono shrink-0 mt-0.5" data-testid="composer-brief-step">
                    {Math.min(step, 5)} / 5
                  </span>
                </div>

                <FieldBlock label="What are you building?" active={step === 0}>
                  <textarea
                    data-testid="composer-brief-description"
                    value={brief.description}
                    onChange={(e) => onUpdate({ description: e.target.value })}
                    placeholder="A sentence or two about your idea…"
                    rows={3}
                    className={`${inputCls} resize-none`}
                  />
                </FieldBlock>

                {step >= 1 && (
                  <FieldBlock label="Project type" active={step === 1}>
                    <div className="flex flex-wrap gap-1.5">
                      {PROJECT_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`composer-brief-type-${t}`}
                          onClick={() => onUpdate({ type: t })}
                          className={`whitespace-nowrap text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                            brief.type === t
                              ? 'bg-accent text-accent-text border-accent'
                              : 'bg-bg text-textp border-border hover:border-accentLine'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </FieldBlock>
                )}

                {step >= 2 && (
                  <FieldBlock label="Name" active={step === 2}>
                    <input
                      data-testid="composer-brief-name"
                      value={brief.name}
                      onChange={(e) => onUpdate({ name: e.target.value })}
                      placeholder="My awesome project"
                      className={inputCls}
                    />
                  </FieldBlock>
                )}

                {step >= 3 && detail && (
                  <FieldBlock label={detail.label} active={step === 3}>
                    <input
                      data-testid="composer-brief-detail"
                      value={brief.detail}
                      onChange={(e) => onUpdate({ detail: e.target.value })}
                      placeholder={detail.placeholder}
                      className={inputCls}
                    />
                  </FieldBlock>
                )}

                {step >= 4 && (
                  <FieldBlock label="Must-have features" active={step === 4}>
                    <textarea
                      data-testid="composer-brief-features"
                      value={brief.features}
                      onChange={(e) => onUpdate({ features: e.target.value })}
                      placeholder="Login, payments, dark mode…"
                      rows={3}
                      className={`${inputCls} resize-none`}
                    />
                  </FieldBlock>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
